import {
  Accordion,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Link,
  Select,
  Spinner,
  Stack,
  useToast,
} from '@chakra-ui/react';
import MainCard from '../../../../../components/MainCard/Index';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ReactNode, useContext, useEffect } from 'react';
import { ContextAuth } from '../../../../../contexts/Authentication';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import AccordionTemplate from '../../../../../components/AccordionTemplate/Index';
import { useRequestGetDataToRegistration } from '../../../../../services/hooks/CandidateArea/useGetDataToRegistration';
import { FieldErrors, FieldValues, FormProvider, useForm } from 'react-hook-form';
import MessageError from '../../../../../components/MessageError';
import { TextL, TextXS, TextXXS } from '../../../../../components/Text/Index';
import { useRequestGetExemptionType } from '../../../../../services/hooks/Exemptions/useRequestGetExemptionType';
import MarrowDonnor from '../MarrowDonnor/Index';
import NIS from '../NIS/Index';
import { useGetRegistration } from '../../../../../services/hooks/Registration/useGetRegistration';
import {
  FileCollection,
  validateMaxFile,
  validateTypeFile,
  validationSizeFile,
} from '../../../../../utils/validationForm';
import { useCreateExemption } from '../../../../../services/hooks/Exemptions/useCreateExemption';
import MessageForUser from '../../../../../components/pages/SelectiveProcess/MessageForUser';
import { isValidUUID } from '../../../../../utils/functions';
import CreateRegistrationMobilePage, { ERegistrationType } from '../../../Registration/Mobile';
import { UseRegistration } from '../../../../../Store/useRegistration';
import { useRequestDetailSelectiveProcess } from '../../../../../services/hooks/SelectiveProcess/useRequestDetailSelectiveProcess';
import EconomicHyposufficiency from '../EconomicHyposufficiency';
import {
  getExemptionAttachmentError,
  getExemptionType,
  isBoneMarrowExemption,
  isCadunicoExemption,
  isEconomicExemption,
} from '../../../../../services/hooks/Exemptions/economicHyposufficiency';
import ExistingExemptionAttachments from '../ExistingExemptionAttachments';

const OnlyExemption: React.FC = () => {
  const navigate = useNavigate();
  const { onResetRegistration, setRegistrationId, setSelectiveProcessId } = UseRegistration();
  const { signOut, candidate, isMobile } = useContext(ContextAuth);

  const toast = useToast();
  const url = useLocation().pathname.split('/')[2];

  const { registrationId: editRegistrationId, id: exemptionRegistrationId } = useParams();
  const registrationId = editRegistrationId || exemptionRegistrationId || '';
  const registration = useGetRegistration(registrationId);

  const selectiveProcessId = isValidUUID(url) && url;

  const spID = isValidUUID(url) ? url : '';

  const getExemption = useRequestGetExemptionType(spID);

  const selectiveProcessData = useRequestGetDataToRegistration(selectiveProcessId || '');

  const spDetail = useRequestDetailSelectiveProcess(selectiveProcessId || '');

  const { mutateAsync, isPending } = useCreateExemption();

  const schema = Yup.object().shape({
    selective_process_id: Yup.string(),
    nis: Yup.string()
      .test('isRequired', 'NIS é obrigatório', (value, context) => {
        const type = getExemptionType(context.parent.type_exemption, getExemption.data);
        return !isCadunicoExemption(type?.name) || !selectiveProcessData.data?.request_nis || Boolean(value?.trim());
      })
      .test('minLength', 'O campo deve ter 11 dígitos', (value, ctx) => {
        const type = getExemptionType(ctx.parent.type_exemption, getExemption.data);
        return !isCadunicoExemption(type?.name) || !selectiveProcessData.data?.request_nis || /^\d{11}$/.test(value || '');
      }),
    redome_code: Yup.string().test('isRequired', 'Campo é obrigatório', (value, context) => {
      const type = getExemptionType(context.parent.type_exemption, getExemption.data);
      return !isBoneMarrowExemption(type?.name) || !selectiveProcessData.data?.request_redome || Boolean(value?.trim());
    }),
    is_declaration_veracity: Yup.boolean().test(
      'isRequired',
      'Para continuar, é necessario aceitar o termo de veracidade das informações!',
      (accept) => {
        return accept;
      },
    ),
    type_exemption: Yup.string().test('required', 'Campo obrigatorio', (value) => !!value),
    subtype: Yup.string().optional(),
    // subtype: Yup.string().test('economic-subtype', 'Selecione uma situação.', (value, context) => {
    //   const type = getExemptionType(context.parent.type_exemption, getExemption.data);
    //   return !isEconomicExemption(type?.name) || Boolean(value);
    // }),
    documents: Yup.array().test(
      'economic-documents',
      'Adicione ao menos um documento com descrição.',
      (value, context) => {
        const type = getExemptionType(context.parent.type_exemption, getExemption.data);
        if (!isEconomicExemption(type?.name)) return true;
        const existing = (context.parent.existingExemptionAttachments || []).filter(
          (item: { id: string }) => !context.parent.removedExemptionAttachmentIds?.includes(item.id),
        );
        return Boolean(
          (existing.length || value?.length) && (value || []).every((item) => item?.file && item?.description?.trim()),
        );
      },
    ),
    existingExemptionAttachments: Yup.array(),
    originalExemptionTypeId: Yup.string(),
    removedExemptionAttachmentIds: Yup.array(),
    attachmentExemption: Yup.mixed<FileCollection>()
      .test('Required', 'Anexe os documentos exigidos para o tipo de isenção selecionado.', function (file, context) {
        const type = getExemptionType(context.parent.type_exemption, getExemption.data);
        const listFile = file as FileCollection;
        const hasExisting = (context.parent.existingExemptionAttachments || []).some(
          (item: { id: string }) => !context.parent.removedExemptionAttachmentIds?.includes(item.id),
        );
        const message = getExemptionAttachmentError({
          exemptionName: type?.name,
          fileCount: listFile?.length ?? 0,
          requiresCadunicoDocument: selectiveProcessData.data?.request_nis_document ?? false,
          hasActiveExistingAttachment: hasExisting,
        });
        return message ? this.createError({ message }) : true;
      })
      .test('validationSizeFile', 'Tamanho máximo para arquivos é de 2MB', (file, context) => {
        if (isEconomicExemption(getExemptionType(context.parent.type_exemption, getExemption.data)?.name)) return true;
        const listFile = file as FileCollection;

        if (listFile?.length > 0) {
          return validationSizeFile(listFile);
        }

        return true;
      })
      .test('validationMaxFile', 'Quantidade máxima de arquivos é de 5 arquivos', (file, context) => {
        if (isEconomicExemption(getExemptionType(context.parent.type_exemption, getExemption.data)?.name)) return true;
        const listFile = file as FileCollection;

        if (listFile?.length > 0) {
          return validateMaxFile(listFile);
        }
        return true;
      })
      .test('validationTypeFile', 'Formato de arquivo não suportado', (file, context) => {
        if (isEconomicExemption(getExemptionType(context.parent.type_exemption, getExemption.data)?.name)) return true;
        const listFile = file as FileCollection;

        if (listFile?.length > 0) {
          return validateTypeFile(listFile);
        }
        return true;
      }),
  });

  useEffect(() => {
    setRegistrationId(registrationId);
    setSelectiveProcessId(url);

    return () => {
      onResetRegistration();
    };
  }, []);

  const listChecked: (string | boolean)[] = [];
  const listObservation: (string | boolean | undefined)[] = [];

  selectiveProcessData.data?.special_condition.map((s) => {
    let isSelected = false;
    let obs = '';

    registration.data?.special_conditions.forEach((r) => {
      if (r.id === s.id) {
        isSelected = true;
        obs = r.observations;
      }
    });

    if (isSelected) {
      listChecked.push(s.id);
      listObservation.push(obs);
    } else {
      listChecked.push(false);
      listObservation.push('');
    }
  });

  const hookForms = useForm({
    resolver: yupResolver(schema),
    values: {
      type_exemption: registration.data?.analysis_exemption?.type
        ? JSON.stringify(registration.data.analysis_exemption.type)
        : '',
      // subtype: registration.data?.analysis_exemption?.subtype?.id || '',
      documents: [],
      existingExemptionAttachments: registration.data?.analysis_exemption?.attachments || [],
      originalExemptionTypeId: registration.data?.analysis_exemption?.type?.id || '',
      removedExemptionAttachmentIds: [],
      nis: registration.data?.analysis_exemption?.nis || candidate?.nis || '',
      redome_code: registration.data?.analysis_exemption?.redome_code || '',
      is_declaration_veracity: Boolean(registrationId),
      selective_process_id: selectiveProcessId || '',
    },
  });

  const newRegistration = (data: FieldValues) => {
    if (isMobile) {
      const exemptionType = getExemptionType(data.type_exemption, getExemption.data);

      data = {
        ...data,
        type_exemption: JSON.stringify(exemptionType),
      };
    }

    mutateAsync({
      data,
      exemptionId: registration.data?.analysis_exemption?.id,
    })
      .then(() => {
        if (registrationId) {
          toast({
            title: 'Sucesso',
            description: 'Isenção Editada com sucesso',
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
          navigate(-1);
          return;
        }

        toast({
          title: 'Sucesso',
          description: 'Isenção criada com sucesso!',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        navigate('/painel/isencoes');
      })
      .catch((error) => {
        toast({
          title: 'Atenção',
          description:
            error.response?.data?.message ||
            error.response?.data?.detail ||
            error.message ||
            'Não foi possível salvar a isenção.',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const onInvalid = (errors: FieldErrors) => {
    const firstError = Object.values(errors).find((error) => error?.message);
    toast({
      title: 'Confira os dados da isenção',
      description:
        typeof firstError?.message === 'string'
          ? firstError.message
          : 'Preencha os campos obrigatórios antes de salvar.',
      status: 'warning',
      duration: 5000,
      isClosable: true,
    });
  };

  if (!selectiveProcessId) navigate(-1);

  const navProps = {
    registrationId: '',
  };

  const handleShowForm = (): ReactNode => {
    const form = hookForms.watch('type_exemption');

    if (form) {
      let formToShow;
      try {
        formToShow = JSON.parse(form);
      } catch (error) {
        formToShow = getExemption.data?.find((exemption) => exemption.id === form);
      }

      if (isEconomicExemption(formToShow?.name)) {
        return (
          <FormProvider {...hookForms}>
            <EconomicHyposufficiency />
          </FormProvider>
        );
      }
      if (isBoneMarrowExemption(formToShow?.name)) {
        return (
          <MarrowDonnor
            hookForms={hookForms}
            // onSetValue={hookForms.setValue}
            exemptionId={formToShow?.id}
            registrationId={navProps?.registrationId || ''}
            redomeRequired={selectiveProcessData.data?.request_redome ?? false}
          />
        );
      } else {
        return (
          <NIS
            hookForms={hookForms}
            hideButton
            // onSetValue={hookForms.setValue}
            cpf={candidate?.cpf || ''}
            birth_date={candidate?.date_of_birth.br || ''}
            exemptionId={formToShow.id}
            exemptionName={formToShow.name}
            registrationId={navProps?.registrationId || ''}
            nisRequired={selectiveProcessData.data?.request_nis ?? false}
            documentRequired={selectiveProcessData.data?.request_nis_document ?? false}
          />
        );
      }
    }

    return false;
  };

  if (isMobile)
    return (
      <FormProvider {...hookForms}>
        <CreateRegistrationMobilePage
          onSubmitForm={newRegistration}
          onInvalidForm={onInvalid}
          isLoading={selectiveProcessData.isLoading || registration.isLoading}
          sendingForm={isPending}
          registrationType={ERegistrationType.ONLY_EXEMPTION}
        />
      </FormProvider>
    );

  const isLoading = registration.isLoading;

  if (spDetail.data?.general_info.exemption_registration_creation_type == 'unified') navigate(-1);

  return (
    <MainCard
      title="PROCESSO SELETIVO - "
      subtitle={registrationId ? 'MODIFICAR SOLICITAÇÃO DE ISENÇÃO' : 'FORMULÁRIO DE SOLICITAÇÃO DE ISENÇÃO'}
    >
      {(isLoading && (
        <Stack flex={1} justify="center" align="center">
          <Spinner />
        </Stack>
      )) || (
        <Flex
          as="form"
          onSubmit={hookForms.handleSubmit(newRegistration, onInvalid)}
          noValidate
          flexDir="column"
          px={['10px', '10px', '10px', '50px', '50px']}
          gap={10}
        >
          {selectiveProcessData?.data?.exemption_request_notices && (
            <MessageForUser mt={5} informationText={selectiveProcessData?.data?.exemption_request_notices || ''} />
          )}
          <Flex
            justifyContent="space-between"
            alignItems="left"
            flexDir={['column', 'column', 'column', 'row', 'row']}
            overflow="hidden"
          >
            <Stack color="darkGrey">
              <TextXS>{selectiveProcessData.data?.selective_process_type_name || ''}</TextXS>
              <TextL>{selectiveProcessData.data?.name || ''}</TextL>
            </Stack>
          </Flex>
          <Stack>
            <Accordion display="flex" flexDir="column" gap={2} allowMultiple defaultIndex={[0, 1, 2, 3, 4]}>
              <AccordionTemplate title="DADOS PESSOAIS" border="1px solid" borderColor="primaryColor" borderRadius={6}>
                <Flex px={10} mb={5} flexDir={['column', 'column', 'row', 'row', 'row']}>
                  <Stack w="100%" p={3}>
                    <TextXS>
                      <strong>Nome completo:</strong> {candidate?.user?.full_name || '--'}
                    </TextXS>
                    <Stack gap={0}>
                      <TextXS>
                        <strong>{candidate?.is_foreign ? 'Passaporte' : 'CPF'}:</strong>{' '}
                        {candidate?.is_foreign ? candidate?.passport_number : candidate?.cpf || '--'} <br />
                      </TextXS>
                      <TextXXS ml="30px" as={'span'}>
                        Não é seu {candidate?.is_foreign ? 'passaporte?' : 'CPF?'}{' '}
                        <Link
                          onClick={() => {
                            signOut();
                            navigate('/');
                          }}
                          as={'span'}
                          color="colorLink"
                        >
                          clique aqui
                        </Link>
                      </TextXXS>
                    </Stack>
                    <TextXS>
                      <strong>Data de nascimento:</strong> {candidate?.date_of_birth.br || '--'}
                    </TextXS>
                    <TextXS>
                      <strong>E-mail:</strong> {candidate?.user?.email || '--'}
                    </TextXS>
                    <TextXS>
                      <strong>{candidate?.document_type?.name || '--'}</strong>
                      {': '}
                      {candidate?.document_number || '--'} {candidate?.document_issuing_body}{' '}
                      {candidate?.document_issuing_body_uf?.initials || ''}
                    </TextXS>
                    <TextXS>
                      <strong>Sexo:</strong> {candidate?.gender?.name || '--'}
                    </TextXS>
                  </Stack>
                  <Stack w="100%" p={3}>
                    <TextXS>
                      <strong>CEP:</strong> {candidate?.address_zip_code || '--'}
                    </TextXS>
                    <TextXS>
                      <strong>Endereço:</strong> {candidate?.address_street || '--'}
                    </TextXS>
                    <TextXS>
                      <strong>Número:</strong> {candidate?.address_number || '--'}
                    </TextXS>
                    <TextXS>
                      <strong>Complemento: </strong>
                      {candidate?.address_complement || '--'}
                    </TextXS>
                    <TextXS>
                      <strong>Bairro:</strong> {candidate?.address_neighborhood || '--'}
                    </TextXS>
                    <TextXS>
                      <strong>Cidade: </strong>
                      {candidate?.address_city?.name || '--'} / {candidate?.address_state?.initials || '--'}
                    </TextXS>
                    <TextXS>
                      <strong>Telefone:</strong> {candidate?.phone || '--'}
                    </TextXS>
                    <TextXS>
                      <strong>Celular:</strong> {candidate?.cellphone || '--'}
                    </TextXS>
                  </Stack>
                </Flex>
                <Stack p={2}>
                  <TextXS>
                    <strong>ATENÇÃO: </strong>Os dados acima estão incorretos? caso estejam,{' '}
                    <Link
                      onClick={() => {
                        navigate('/painel/meus-dados');
                      }}
                      color="colorLink"
                    >
                      clique aqui
                    </Link>{' '}
                    e atualize seu cadastro.
                  </TextXS>
                </Stack>
              </AccordionTemplate>

              <AccordionTemplate
                border="1px solid"
                borderColor="primaryColor"
                borderRadius={6}
                title="DADOS DA SOLICITAÇÃO DE ISENÇÃO"
              >
                <Stack py={10}>
                  <FormControl>
                    <FormLabel>Tipo de solicitação de isenção</FormLabel>
                    <Select
                      {...hookForms.register('type_exemption', {
                        onChange: (event) => {
                          hookForms.clearErrors([
                            'type_exemption',
                            'subtype',
                            'documents',
                            'existingExemptionAttachments',
                            'removedExemptionAttachmentIds',
                            'attachmentExemption',
                            'nis',
                            'redome_code',
                          ]);
                          hookForms.setValue('subtype', '', { shouldDirty: true, shouldValidate: false });
                          hookForms.setValue('documents', [], { shouldDirty: true, shouldValidate: false });
                          hookForms.setValue('attachmentExemption', [], { shouldDirty: true, shouldValidate: false });
                          hookForms.setValue('nis', '', { shouldDirty: true, shouldValidate: false });
                          hookForms.setValue('redome_code', '', { shouldDirty: true, shouldValidate: false });
                          const originalType = registration.data?.analysis_exemption?.type;
                          hookForms.setValue(
                            'removedExemptionAttachmentIds',
                            originalType &&
                              getExemptionType(event.target.value, getExemption.data)?.id !== originalType.id
                              ? (registration.data?.analysis_exemption?.attachments || []).map(
                                  (attachment) => attachment.id,
                                )
                              : [],
                            { shouldDirty: true, shouldValidate: false },
                          );
                        },
                      })}
                      w="100%"
                      maxW="350px"
                    >
                      <option hidden value="">
                        Selecione
                      </option>
                      {getExemption?.data?.map((exemption, index) => (
                        <option key={index} value={JSON.stringify(exemption)}>
                          {exemption?.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <MessageError>{hookForms.formState.errors.type_exemption?.message}</MessageError>
                  {handleShowForm()}
                  {registrationId &&
                    !isEconomicExemption(
                      getExemptionType(hookForms.watch('type_exemption'), getExemption.data)?.name,
                    ) && (
                      <FormProvider {...hookForms}>
                        <ExistingExemptionAttachments />
                      </FormProvider>
                    )}
                </Stack>
                <Checkbox
                  isChecked={hookForms.watch('is_declaration_veracity')}
                  {...hookForms.register('is_declaration_veracity')}
                  alignItems="flex-start"
                  mb={5}
                >
                  <TextXS overflow="auto">
                    Declaração de veracidade das informações, conforme a Lei nº 13.656/2018, ciente das penalidades por
                    falsidade.
                  </TextXS>
                </Checkbox>
                <MessageError>{hookForms.formState?.errors.is_declaration_veracity?.message}</MessageError>
              </AccordionTemplate>
            </Accordion>
            <Stack flexDir="row" justify="flex-end" py={5}>
              <Button
                w={'100px'}
                onClick={() => {
                  navigate('/');
                }}
              >
                Cancelar
              </Button>
              <Button
                isDisabled={isPending || selectiveProcessData.isLoading}
                type="submit"
                bg="colorLink"
                color="white"
                w={'100px'}
                _hover={'none'}
              >
                {(isPending && <Spinner />) || (registrationId && 'Salvar') || 'Continuar'}
              </Button>
            </Stack>
          </Stack>
        </Flex>
      )}
    </MainCard>
  );
};

export default OnlyExemption;
